import { useState } from 'react';
import type { OutreachAngle } from '../../../api/salesResearch';

/**
 * The part of the brief a rep acts on, so it renders as fields rather than
 * prose — it is stored as structured JSON precisely so it can look like this.
 */
export function OutreachAngleCard({ outreach }: { outreach: OutreachAngle }) {
  return (
    <section className="rounded-lg border border-accent/20 bg-accent-light/30 p-4 space-y-3">
      <h3 className="text-body-md font-semibold">Outreach angle</h3>

      <Field label="Trigger" value={outreach.trigger} />
      <Field label="Pain hypothesis" value={outreach.painHypothesis} />
      <Field label="Why this persona cares" value={outreach.whyItMattersToPersona} />

      <div>
        <p className="text-body-xs font-medium uppercase tracking-wide text-text-tertiary">
          Proof point
        </p>
        {outreach.proofPoint ? (
          <p className="text-body-sm">
            <span className="font-medium">{outreach.proofPoint.customer}:</span>{' '}
            {outreach.proofPoint.claim}
          </p>
        ) : (
          // Saying so is the correct outcome, not a failure — the library is
          // thin, and a forced customer story is worse than none.
          <p className="text-body-sm text-text-tertiary italic">
            No proof point in the library matches this segment. Don't improvise one.
          </p>
        )}
      </div>

      <CopyableField label="Messaging angle" value={outreach.messagingAngle} />

      <div>
        <p className="text-body-xs font-medium uppercase tracking-wide text-text-tertiary mb-1">
          Subject lines
        </p>
        <div className="space-y-1">
          {outreach.subjectLineOptions.map((s, i) => (
            <CopyRow key={i} value={s} />
          ))}
        </div>
      </div>

    </section>
  );
}

/**
 * The angle above is written for one named lead. Four people work this account,
 * so each function gets its own opener and its own objection, and the opener is
 * copyable because that is what the reader came for.
 *
 * Rendered after the attack plan rather than inside the angle, matching the
 * markdown and the print layout: the plan is read once when the team sets up,
 * these cards are returned to on the day a mail goes out.
 */
export function PersonaMessagingCards({ outreach }: { outreach: OutreachAngle }) {
  const entries = outreach.personaMessaging ?? [];
  if (entries.length === 0) return null;

  return (
    <section className="space-y-3">
      <h3 className="text-body-md font-semibold">Messaging guide by persona</h3>
      {entries.map((m, i) => (
        <div key={i} className="rounded-lg border border-border bg-surface-primary p-3 space-y-2">
          <p className="text-body-sm font-medium">{m.persona}</p>
          <Field label="Why them, now" value={m.whyThemNow} />
          <CopyableField label="Opener" value={m.opener} />
          <Field label="Objection to expect" value={m.objectionToExpect} />
        </div>
      ))}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-body-xs font-medium uppercase tracking-wide text-text-tertiary">{label}</p>
      <p className="text-body-sm">{value}</p>
    </div>
  );
}

function CopyableField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-body-xs font-medium uppercase tracking-wide text-text-tertiary">{label}</p>
      <CopyRow value={value} multiline />
    </div>
  );
}

function CopyRow({ value, multiline }: { value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="flex items-start gap-2">
      <span className={`text-body-sm flex-1 ${multiline ? '' : 'truncate'}`}>{value}</span>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 rounded border border-border px-2 py-0.5 text-body-xs hover:bg-surface-secondary"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}
